// Email-specific extras: subject line (collapsed by default for replies),
// attachment chips, in-reply-to indicator.

import { useState } from "react";
import type { Turn, EmailTurnMeta } from "./types";

interface Props {
  turn: Turn;
  // The conversation's title (= thread subject) so we only render the
  // per-message subject when it differs (i.e. "Re:" got dropped or it's
  // the first message of a fresh thread).
  conversationTitle?: string | null;
}

function humanBytes(n?: number): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function EmailTurnExtras({ turn, conversationTitle }: Props): React.ReactElement | null {
  const meta = (turn.metadata ?? {}) as EmailTurnMeta;
  const subject = meta.subject;
  const attachments = meta.attachments ?? [];
  const isReply = Boolean(meta.in_reply_to);
  const subjectDiffers = Boolean(
    subject &&
      conversationTitle &&
      subject.replace(/^(Re|Fwd|Fw):\s*/i, "").trim() !==
        conversationTitle.replace(/^(Re|Fwd|Fw):\s*/i, "").trim(),
  );
  const [expanded, setExpanded] = useState(false);

  if (!subjectDiffers && attachments.length === 0 && !isReply) {
    return null;
  }

  return (
    <div className="cv-email-extras">
      {subjectDiffers && (
        <div className="cv-email-subject">subject: {subject}</div>
      )}
      {isReply && !subjectDiffers && (
        <span className="cv-thread-tag" title="In reply to a prior message">↳ reply</span>
      )}
      {attachments.length > 0 && (
        <>
          <button
            type="button"
            className="cv-attachments-toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            📎 {attachments.length}
          </button>
          {expanded && (
            <ul className="cv-attachments">
              {attachments.map((a) => (
                <li key={a.filename}>
                  <span className="cv-attachment-name">{a.filename}</span>
                  {a.size_bytes != null && (
                    <span className="cv-attachment-size">
                      {humanBytes(a.size_bytes)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
